/**
 * Per-signal rankings — one panel per signal, all companies ranked within it
 * (2026-09-08).
 *
 * THIS REPLACES THE GROUPED BAR. Lisa, reviewing the first pass on 2026-09-01:
 * "it looks like this is breaking out per company. This isn't helpful... unless
 * it's an entire mentions chart and an entire citation chart aside, then you
 * know who's winning in citations, who's winning in mentions." Grouping by
 * company answered "what is this one company's signal mix" — a question nobody
 * asked — and made the cross-company comparison the report exists for
 * impossible. Transposing it answers hers directly.
 *
 * FORM: emphasis, not categorical (see the `dataviz` form table — "one series is
 * the point, rest are context"). Each panel is a single measure, so the client
 * carries that panel's hue and every competitor is de-emphasis gray. Colour
 * never encodes rank, so a filter or a data change cannot repaint the field.
 *
 * The three panel hues are the categorical palette's first three slots, reused
 * from `GroupedBarList` so Mention/Citation/Recommendation keep one identity
 * across the whole report. Those three slots validate on the all-pairs
 * pairlist in dark mode, which is the strict case for small multiples.
 *
 * STILL NEVER STACKED. The three signals overlap and do not sum (2026-07-13
 * D1); a stacked "signal mix" bar was removed on 2026-07-19 for exactly that
 * reason. Separate panels are the honest way to show three overlapping measures.
 */
import type { SignalRanking } from '@aurora/shared';

const HUES: Record<SignalRanking['key'], string> = {
  mention: '#3987e5',
  citation: '#d95926',
  recommendation: '#199e70',
};

const CONTEXT = '#4a5670';

const BAR_H = 13;
const BAR_GAP = 5;
const LABEL_W = 148;
const TRACK_W = 150;
const CHART_W = LABEL_W + TRACK_W + 42;
/** Longest company label that fits LABEL_W at the label font size without clipping. */
const LABEL_MAX = 17;

function Panel({ ranking }: { ranking: SignalRanking }): JSX.Element {
  const { rows, label, key } = ranking;
  const max = Math.max(1, ...rows.map((r) => r.value));
  const rowH = BAR_H + BAR_GAP;
  const chartH = Math.max(1, rows.length) * rowH;
  const hue = HUES[key];

  return (
    <figure className="signal-panel">
      <figcaption className="signal-panel-title">
        <span className="signal-panel-swatch" style={{ background: hue }} />
        {label}
      </figcaption>
      <svg
        width="100%"
        viewBox={`0 0 ${CHART_W} ${chartH}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label={`${label} ranked by company`}
      >
        {rows.map((r, i) => {
          const y = i * rowH;
          const w = Math.max(r.value > 0 ? 2 : 0, (r.value / max) * TRACK_W);
          return (
            <g key={r.company}>
              <text
                x={LABEL_W - 7}
                y={y + BAR_H / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className={
                  r.isTarget ? 'chart-bar-label is-target' : 'chart-bar-label'
                }
              >
                {r.company.length > LABEL_MAX
                  ? `${r.company.slice(0, LABEL_MAX - 1)}…`
                  : r.company}
              </text>
              <rect
                x={LABEL_W}
                y={y}
                width={TRACK_W}
                height={BAR_H}
                rx={3}
                className="chart-bar-track"
              />
              <rect
                x={LABEL_W}
                y={y}
                width={w}
                height={BAR_H}
                rx={3}
                fill={r.isTarget ? hue : CONTEXT}
              >
                <title>
                  {r.company} — {label}: {r.value}
                </title>
              </rect>
              <text
                x={LABEL_W + Math.max(w, 2) + 5}
                y={y + BAR_H / 2}
                dominantBaseline="middle"
                className={
                  r.isTarget ? 'chart-bar-value is-target' : 'chart-bar-value'
                }
              >
                {r.value}
              </text>
            </g>
          );
        })}
      </svg>
      {ranking.targetAppended && (
        <p className="signal-panel-note">
          You did not rank in the top {rows.length - 1}; your row is shown last
          for comparison.
        </p>
      )}
    </figure>
  );
}

export function SignalRankPanels({
  rankings,
  targetName,
}: {
  rankings: SignalRanking[];
  targetName?: string;
}): JSX.Element | null {
  if (rankings.length === 0) return null;
  return (
    <div className="signal-panels-wrap">
      <div className="signal-panels">
        {rankings.map((r) => (
          <Panel key={r.key} ranking={r} />
        ))}
      </div>
      <p className="muted signal-panel-legend">
        Each panel ranks every company on one signal, so you can read who leads
        on mentions, on citations and on recommendations separately.{' '}
        {targetName ? <strong>{targetName}</strong> : 'You'} is highlighted; the
        other companies are shown in grey for context. The three signals overlap
        and do not sum — one answer can count in all three.
      </p>
    </div>
  );
}
